import { 
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid, 
  IconButton, 
  TextField, 
  Tooltip, 
  Typography 
} from "@mui/material";
import CustomModal, { CustomModalBasicProps, GridItem, GridSection } from "src/components/CustomModal";
import useObjectState from "src/hooks/useObjectState";
import { useEffect, useMemo, useState } from "react";
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { Marker, useJsApiLoader } from "@react-google-maps/api";
import { capitalize } from "lodash";
import { SearchOrganizations, searchOrganizations } from "../service/OrganizationService";
import useValueSetsByIdentifiers from "src/hooks/useValueSetsByIdentifier";
import { useSnackbar } from "notistack";
import { DisabledByDefault, Info } from "@mui/icons-material";
import FhirOrganizationGrid, { ActionsFn, CreateManuallyButton, filterOrgBySearchInput } from "../common/FhirOrganizationGrid";
import { cleanSearchInput } from "src/utils/string";
import { PATH_DASHBOARD } from "src/routes/paths";
import { paramCase } from "change-case";
import Iconify from "src/components/Iconify";
import { mapOrganizationToFhir, placeToAddress, validateInputs } from "../common/util";
import { fhirClient } from "src/App";
import { useNavigate } from "react-router";
import useGridFilters from "src/careflow/ph-core/grid/hook/useGridFilters";
import { Organization, ValueSetComposeIncludeConcept } from "src/careflow/ph-core/fhir-client/resources";
import AddressAutoComplete from "src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/AddressAutoComplete";
import CancelButton from "src/careflow/common/CancelButton";
import { CustomLoadingButton } from "src/careflow/common/CustomButton";
import Map from 'src/careflow/care-coordination/referral/components/workflow-step/AddressChecklistItem/Map';

export interface SearchOrganizationModalProps extends CustomModalBasicProps {
  handlerActionCreate?: Function,
  translate: any,
  configurations: any,
  componentsData: any
}

interface SearchOrganizationState extends SearchOrganizations{
  address: any;
  selectedAddress: any;
  data: any;
}

interface SearchComponentState {
  showAddressFields: boolean;
  showMap: boolean;
  isSearching: boolean;
  isDoneSearching: boolean;
  errorMessage: string | null;
  errors: any;
  isAdding: boolean;
}

const SearchOrganization = ({
  handlerActionCreate, 
  open, 
  onClose,
  translate,
  configurations,
  componentsData,
  ...modalProps 
}: SearchOrganizationModalProps) => {

  const [markerPosition, setMarkerPosition] = useState<{
      lat: number | null;
      lng: number | null;
    }>({
      lat: null,
      lng: null,
    });

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [{
    typeIdentifier,
    identifier,
    name,
    type,
    address,
    street1,
    street2,
    city,
    state,
    zipCode,
    selectedAddress,
    data,
  }, updateState] = useObjectState<SearchOrganizationState>({
    typeIdentifier: null,
    identifier: null,
    name: null,
    type: null,
    address: null,
    street1: null,
    street2: null,
    city: null,
    state: null,
    zipCode: null,
    selectedAddress: null,
    data: []
  });

  const [{
    showAddressFields, 
    showMap,
    isSearching,
    isDoneSearching,
    errorMessage,
    errors,
    isAdding
  }, updateComponentState] = useObjectState<SearchComponentState>({
    showAddressFields: false,
    showMap: false,
    isSearching: false,
    isDoneSearching: false,
    errorMessage: null,
    errors: null,
    isAdding: false
  });

  const { searchTextFieldValue, onSearchTextFieldChange } = useGridFilters({ searchTextFieldValue: null });

  const {
    valueSets: [organizationIdentifierTypesVS, valueSet],
    isLoading: isLoadingVS
  } = useValueSetsByIdentifiers([
    'ph-organization-identifier-types',
    'organization-types'
  ], { autofetch: !!open});

  const { useMasterOrganizationIndex, googleMapsApiKey } = configurations ?? {};

   const JsOptions = useMemo<any>(
    () => ({
      id: 'google-map-script',
      googleMapsApiKey: googleMapsApiKey,
      libraries: ['places'],
    }),
    []
  );

  const { isLoaded: isGoogleAPILoaded } = useJsApiLoader(JsOptions);

  const { placesService } = usePlacesService({ apiKey: googleMapsApiKey });

  const safePlaceService = isGoogleAPILoaded ? placesService : null;

   useEffect(() => {
    if(!isGoogleAPILoaded) return;
    if (selectedAddress !== null) {
      safePlaceService?.getDetails(
        {
          placeId: selectedAddress.place_id,
        },
        (place) => {
          const addressFormated = placeToAddress(place, configurations)
          updateState((prevState) => ({
            ...prevState,
            street1: addressFormated?.line?.[0] ?? '',
            city: addressFormated?.city ?? '',
            state: addressFormated?.state ?? '',
            zipCode: addressFormated?.postalCode ?? '',
            address: addressFormated
          }));
          setMarkerPosition({
            lat: place?.geometry?.location?.lat?.() ?? null,
            lng: place?.geometry?.location?.lng?.() ?? null,
          });
        }
      );
    }
  }, [selectedAddress, isGoogleAPILoaded]);

  useEffect(() => {
    if (!open) {
      updateState({
        typeIdentifier: null,
        identifier: null,
        name: null,
        type: null,
        address: null,
        street1: null,
        street2: null,
        city: null,
        state: null,
        zipCode: null,
        selectedAddress: null,
      });
      updateComponentState({
        showAddressFields: false,
        showMap: false,
        isSearching: false,
        isAdding: false,
        isDoneSearching: false,
        errorMessage: null,
        errors: {}
      })
    }
  },[open]);

  const search = async () => {
    updateComponentState({ errorMessage: null, showMap: false });

    const filters = {
      typeIdentifier,
      identifier,
      name,
      type,
      street1,
      street2,
      city,
      state,
      zipCode
    };

    if (!Object.entries(filters).some(([, value]) => value?.trim?.() || value?.code?.trim?.())
    ){
      return updateComponentState({ 
        errorMessage: translate('details.search.specifyAtLeastOneCriteria') 
      });
    }

    try {
      updateComponentState({ isSearching: true })
      const result = await searchOrganizations(filters, configurations?.fhir?.baseUrl, useMasterOrganizationIndex);
    
      updateState({ data: result ?? [] });
      updateComponentState({ isDoneSearching: true })
    } catch (error) {
      console.log('SearchOrganization error', error);
      enqueueSnackbar('An error has occurred', { variant: 'error' });
    }finally{
      updateComponentState({ isSearching: false })
    }
  };

  const orgsFiltered = useMemo(() => {
    const searchInput = cleanSearchInput(
      searchTextFieldValue ?? ''
    ).toLowerCase();
  
    return (data ?? [])
      .filter(org => filterOrgBySearchInput(searchInput, org));
  }, [data, searchTextFieldValue]);

  const handleAdd = async () => {
    const validateFields = await validateInputs(
      {
        typeIdentifier,
        identifier,
        name,
        address,
        type
      },
      translate,
      componentsData
    );

    if (validateFields !== null) {
      updateComponentState({ errors: validateFields,
        ...(validateFields?.address && { showAddressFields: true })
       })
      return;
    };

    try {
      updateComponentState({ isAdding: true });
      const organizationToSave = mapOrganizationToFhir(
        {
          typeIdentifier,
          identifier,
          name,
          address,
          type
        },
        configurations,
        organizationIdentifierTypesVS 
      );

      const orgSaved = await fhirClient.createOne<Organization>('Organization', organizationToSave);

      if(orgSaved){
        handlerActionCreate?.();
        navigate(PATH_DASHBOARD.fhirOrganization.details(paramCase(orgSaved.id!)));
      }

    } catch (error) {
      console.log('AddOrganization error', error);
      enqueueSnackbar('An error has occurred', { variant: 'error' });
    }finally{
      updateComponentState({ isAdding: false });
    }
  };

  const actions: ActionsFn = ({ row }) => {
    return [
      <Button
        key={row.id}
        component="a"
        href={`${PATH_DASHBOARD.fhirOrganization.details(paramCase(row.id!))}`}
        rel="noopener noreferrer"
        variant="contained"
        endIcon={<Iconify icon="fluent:open-12-filled" />}
        sx={{
          ml: 1,
          textTransform: 'none',
          borderRadius: 2,
          bgcolor: 'primary.main'
        }}
      >
        {translate('details.search.view')}
      </Button>
    ];
  };

  return (
    <>
      <CustomModal
        open={open}
        title={translate('details.search.title')}
        breadcrumbs={[]}
        onSave={() => {}}
        showSaveButton={false}
        showCancelButton={false}
        onCancel={onClose as Function}
        onClose={onClose}
        containerSx={[{ width: '90vw', minHeight: '54vh', overflow: 'scroll', pb: 0 }]}
        {...modalProps}
      >
        <Grid container sx={{ width: '100%', height: '100%' }} my={3} spacing={2}>
          <Grid item xs={5}>
            <GridSection mt={0}>
              <GridItem>
                <Autocomplete
                  fullWidth
                  disabled={!!isLoadingVS}
                  value={typeIdentifier}
                  onChange={(_: React.SyntheticEvent, type) => {
                    updateComponentState({ errors: { typeIdentifier: null } });
                    updateState({ typeIdentifier: type });
                  }}
                  options={organizationIdentifierTypesVS?.asList() ?? []}
                  getOptionLabel={(type: ValueSetComposeIncludeConcept) =>
                    capitalize(type?.display as string)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('details.search.typeIdentifier')}
                      variant="outlined"
                      error={!!errors?.typeIdentifier}
                      helperText={errors?.typeIdentifier ?? ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {!!isLoadingVS ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </GridItem>
              <GridItem>
                <TextField
                  fullWidth
                  id="identifier"
                  name="identifier"
                  label={translate('details.search.identifier')}
                  placeholder={translate('details.search.identifier')}
                  value={identifier}
                  error={!!errors?.identifier}
                  helperText={errors?.identifier ?? ''}
                  onChange={(newValue) => {
                    updateComponentState({ errors: { identifier: null } });
                    updateState({ identifier: newValue?.target?.value });
                  }}
                />
              </GridItem>
            </GridSection>
            <GridSection>
              <GridItem xs={12}>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label={translate('details.search.name')}
                  placeholder={translate('details.search.name')}
                  error={!!errors?.name}
                  helperText={errors?.name ?? ''}
                  value={name}
                  onChange={(newValue) => {
                    updateComponentState({ errors: { name: null } });
                    updateState({ name: newValue?.target?.value });
                  }}
                />
              </GridItem>
            </GridSection>
            <GridSection>
              <GridItem xs={12}>
                <Autocomplete
                  disabled={!!isLoadingVS}
                  value={type}
                  fullWidth
                  onChange={(_: React.SyntheticEvent, newValue) => {
                    updateComponentState({ errors: { type: null } });
                    updateState({ type: newValue });
                  }}
                  options={valueSet?.asList() ?? []}
                  getOptionLabel={(type: ValueSetComposeIncludeConcept) =>
                    capitalize(type?.display as string)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('details.search.type')}
                      variant="outlined"
                      error={!!errors?.type}
                      helperText={errors?.type ?? ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {!!isLoadingVS ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </GridItem>
            </GridSection>

            {isGoogleAPILoaded && 
              <GridSection>
                <GridItem xs={12}>
                  <AddressAutoComplete
                    label={`${translate('details.search.address')}`}
                    address={selectedAddress}
                    onAddressChange={(value) => {
                      if(!value) {
                        updateState({ 
                          street1: null,
                          street2: null,
                          city: null,
                          state: null,
                          zipCode: null,
                          address: null,
                          selectedAddress: null,
                      });
                      return;
                      }
                      updateComponentState({ errors: { address: null } });
                      updateState({ selectedAddress: value });
                    }}
                    onFocus={() => {
                      updateComponentState({ showAddressFields: true, showMap: true });
                    }}
                  />
                </GridItem>
              </GridSection>
            }

            {showAddressFields && (
              <>
                <GridSection>
                  <GridItem>
                    <TextField
                      fullWidth
                      id="street1"
                      name="street1"
                      label={`${translate('details.search.street1')}`}
                      placeholder={translate('details.search.street1')}
                      value={street1 ?? ''}
                      error={!!errors?.address}
                      helperText={errors?.address ?? ''}
                      onChange={(value) => {
                        updateState({ street1: value?.target?.value });
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </GridItem>
                  <GridItem>
                    <TextField
                      fullWidth
                      id="street2"
                      name="street2"
                      label={translate('details.search.street2')}
                      placeholder={translate('details.search.street2')}
                      value={street2 ?? ''}
                      onChange={(value) => {
                        updateState({ street1: value?.target?.value });
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </GridItem>
                </GridSection>
                <GridSection>
                  <GridItem>
                    <TextField
                      fullWidth
                      id="city"
                      name="city"
                      label={`${translate('details.search.city')}`}
                      placeholder={translate('details.search.city')}
                      value={city ?? ''}
                      onChange={(value) => {
                        updateState({ street1: value?.target?.value });
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </GridItem>
                  <GridItem>
                    <TextField
                      fullWidth
                      id="state"
                      name="state"
                      label={`${translate('details.search.state')}`}
                      placeholder={translate('details.search.state')}
                      value={state ?? ''}
                      onChange={(value) => {
                        updateState({ street1: value?.target?.value });
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </GridItem>
                </GridSection>
                <GridSection>
                  <GridItem>
                    <TextField
                      fullWidth
                      id="zipCode"
                      name="zipCode"
                      label={`${translate('details.search.zipCode')}`}
                      placeholder={translate('details.search.zipCode')}
                      value={zipCode ?? ''}
                      onChange={(value) => {
                        updateState({ street1: value?.target?.value });
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </GridItem>
                  {showMap && 
                    <GridItem sx={{ alignContent: 'center' }}>
                      <IconButton
                        color="error"
                        onClick={() => updateComponentState({ showMap: false })}
                        title="Close Map"
                      >
                        <DisabledByDefault />
                      </IconButton>
                    </GridItem>
                  }
                </GridSection>
              </>
            )}
            <GridSection xs={12}>
              {!!errorMessage && (
                <Box>
                  <Typography
                    color={'#ff3333'}
                    sx={{
                      fontWeight: 'bold',
                      wordBreak: 'normal',
                      whiteSpace: 'pre-line',
                      fontSize: '0.9rem',
                    }}
                  >
                    {errorMessage}
                  </Typography>
                </Box>
              )}
            </GridSection>

            <GridSection>
              <GridItem
                xs={12}
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}
              >
                <CancelButton
                  onClick={() => {
                    onClose?.({}, 'backdropClick');
                  }}
                />
                <Tooltip title={translate('details.search.search')}>
                  <span>
                    <CustomLoadingButton
                      disabled={isSearching}
                      sx={{
                        marginLeft: 1,
                      }}
                      loading={isSearching}
                      onClick={search}
                      variant="contained"
                    >
                      {translate('details.search.search')}
                    </CustomLoadingButton>
                  </span>
                </Tooltip>
              </GridItem>
            </GridSection>
          </Grid>
          <Grid item xs={7} sx={{ marginTop: 0 }}>
            <Box sx={{ height: '100%' }}>
              {showMap && isGoogleAPILoaded ? (
                <Map
                  zoom={5}
                  center={{
                    lat: markerPosition.lat ?? 38.3966908,
                    lng: markerPosition.lng ?? -99.525901,
                  }}
                  isLoaded={isGoogleAPILoaded}
                >
                  {markerPosition.lat !== null && markerPosition.lng !== null ? (
                    <Marker
                      position={{
                        lat: markerPosition.lat,
                        lng: markerPosition.lng,
                      }}
                    />
                  ) : null}
                </Map>
              ) : (
                <>
                  {isDoneSearching ? (
                    !!data?.length ? (
                      <Box sx={{ position: 'relative', top: -10 }}>
                        <Typography variant="h6">{translate('details.search.searchResults')}</Typography>
                          <Box
                            sx={{
                              paddingY: 2,
                              paddingX: 2,
                              backgroundColor: '#cafdf5',
                              marginTop: 2,
                              borderRadius: 1,
                              gap: 12,
                              marginBottom: 2,
                            }}
                          >
                            <Box
                              sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                            >
                              <Info htmlColor="#00b8d9" sx={{ marginRight: 1.5 }} />
                              <Typography
                                variant="body2"
                                sx={{ color: '#003768', fontSize: '0.94rem' }}
                              >
                                {translate('details.search.resultsMessage')}
                              </Typography>

                              <CreateManuallyButton
                                sx={{ marginLeft: 1.5 }}
                                onClick={handleAdd}
                                loading={isAdding}
                                disabled={isAdding}
                                title={translate('details.search.createManually')}
                              />
                            </Box>
                          </Box>
                        <FhirOrganizationGrid 
                          organizations={orgsFiltered}
                          searchTextFieldValue={searchTextFieldValue}
                          onSearchTextFieldChange={onSearchTextFieldChange}
                          translate={translate}
                          actions={actions}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-end'
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.94rem', color: '#919eab' }}
                          >
                            {data.length} {translate('details.search.resultsMatch')}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography variant="h4" sx={{ textAlign: 'center' }}>
                          {translate('details.search.noResultsFound')}
                        </Typography>
                        <Typography variant="body2" sx={{ textAlign: 'center', fontSize: '1rem' }}>
                          {translate('details.search.couldntFindOrganization')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1rem' }}
                        >
                          {translate('details.search.completeFormToCreate')}
                        </Typography>
                        <CreateManuallyButton
                          sx={{ marginTop: 1 }}
                          onClick={handleAdd}
                          disabled={isAdding}
                          loading={isAdding}
                          title={translate('details.search.createManually')}
                        />
                      </Box>
                    )
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography variant="h4" sx={{ textAlign: 'center' }}>
                        {translate('details.search.useFieldsOnTheLeft')}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </CustomModal>
    </>
  )
}

export default SearchOrganization;
